'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faTableColumns } from '@fortawesome/pro-solid-svg-icons';
import { PulseToggle } from 'src/components';
import { useAnalytics } from 'src/hooks/analytics';
import { useLogging } from 'src/context/logging';
import type { PayerGroupBy } from 'src/redux/slices/analyticsSlice';
import { AnalyticsModeToggle } from '../AnalyticsModeToggle';
import { AnalyticsVolumeDropdown } from '../AnalyticsVolumeDropdown';
import { AnalyticsAspButton } from '../AnalyticsAspButton';
import {
  PAYER_GROUP_BY_OPTIONS,
  PROVIDER_SITES_OPTIONS,
  REFERRING_DISABLED_FOR_SITES_TOOLTIP,
  SITES_DISABLED_FOR_REFERRING_TOOLTIP,
} from '../../constants';

type VolumeOption = 'claims' | 'patients' | 'providers' | 'sites';

type SectionKey =
  | 'mode'
  | 'volume'
  | 'providerSites'
  | 'payerGroup'
  | 'asp';

/**
 * Headings shown above each control when the panel renders.
 *
 * `providerSites` and `volume` deliberately share the "Volume Type" heading:
 * only Average Reimbursement sets `showProviderSitesToggle`, and it turns the
 * volume section off, so the two never render together.
 */
const SECTION_LABELS: Record<SectionKey, string> = {
  mode: 'Source',
  volume: 'Volume Type',
  providerSites: 'Volume Type',
  payerGroup: 'Display By',
  asp: 'Average Sale Price',
};

const SECTION_TEST_IDS: Record<SectionKey, string> = {
  mode: 'view-section-mode',
  volume: 'view-section-volume',
  providerSites: 'view-section-provider-sites',
  payerGroup: 'view-section-payer-group',
  asp: 'view-section-asp',
};

export interface AnalyticsViewButtonProps {
  location?: string;
  showModeSelector?: boolean;
  showVolumeToggle?: boolean;
  showProviderSitesToggle?: boolean;
  showPayerGroupToggle?: boolean;
  /** Adds the "Configure ASP" drawer trigger as a section. */
  showAspButton?: boolean;
  /**
   * `expanded` drops the trigger and lays the sections out flat — used when the
   * header renders this inside its "..." menu, where a nested dropdown would be
   * an extra click for no gain.
   */
  layout?: 'auto' | 'expanded';
  /** Returns true for modes that can't be selected in the current context. */
  isModeDisabled?: (value: string) => boolean;
  /** Returns true for volume types that can't be selected in the current context. */
  isVolumeTypeDisabled?: (value: VolumeOption) => boolean;
}

/**
 * Secondary control panel for how the table slices its data.
 *
 * These controls (mode, volume type, payer breakdown) used to sit inline in the
 * header, which ran off the screen once there were more than a couple of them.
 * They live here instead so the header row stays a fixed, predictable width no
 * matter how many slicing dimensions a report grows.
 */
const AnalyticsViewButton: React.FC<AnalyticsViewButtonProps> = ({
  location,
  showModeSelector = false,
  showVolumeToggle = false,
  showProviderSitesToggle = false,
  showPayerGroupToggle = false,
  showAspButton = false,
  layout = 'auto',
  isModeDisabled,
  isVolumeTypeDisabled,
}) => {
  const log = useLogging();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const {
    modeType = 'treating',
    volumeTypes = [],
    payerGroupBy = 'payer_type',
    setVolumeTypes,
    setModeType,
    setPayerGroupBy,
  } = useAnalytics(location) || {};

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        panelRef.current &&
        !panelRef.current.contains(target)
      ) {
        // A control inside the panel may portal its own menu to document.body,
        // which makes that menu a DOM sibling of the panel rather than a
        // descendant of it. Closing on mousedown would unmount the menu before
        // its option's click could land, so the selection never applied
        // (PTRI-233). Same guard the header uses for its "..." menu.
        const targetElement = event.target as HTMLElement;
        const isPortaledDropdown =
          targetElement.closest('[class*="z-[99999]"]') ||
          targetElement.closest('[class*="z-50"]');

        if (!isPortaledDropdown) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleOpen = useCallback(() => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Anchored to the right edge so the panel never runs off-screen when the
      // button sits near the end of the header row.
      setPanelPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
      log.event('Analytics view panel opened', {
        source: 'AnalyticsViewButton',
        location,
      });
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, log, location]);

  const handleModeChange = useCallback(
    (value: 'treating' | 'sending') => {
      log.event('Analytics mode changed', {
        source: 'AnalyticsViewButton',
        location,
        value,
      });
      setModeType(value);
    },
    [setModeType, log, location]
  );

  const handleVolumeTypesChange = useCallback(
    (value: VolumeOption[]) => {
      log.event('Analytics volume types changed', {
        source: 'AnalyticsViewButton',
        location,
        value,
      });
      setVolumeTypes(value);
    },
    [setVolumeTypes, log, location]
  );

  const handleProviderSitesChange = useCallback(
    (value: string) => {
      log.event('Analytics provider/sites toggle changed', {
        source: 'AnalyticsViewButton',
        location,
        value,
      });
      setVolumeTypes([value as 'providers' | 'sites']);
    },
    [setVolumeTypes, log, location]
  );

  const handlePayerGroupByChange = useCallback(
    (value: PayerGroupBy) => {
      log.event('Analytics payer grouping changed', {
        source: 'AnalyticsViewButton',
        location,
        value,
      });
      setPayerGroupBy(value);
    },
    [setPayerGroupBy, log, location]
  );

  const sections = [
    showModeSelector && 'mode',
    showVolumeToggle && 'volume',
    showProviderSitesToggle && 'providerSites',
    showPayerGroupToggle && 'payerGroup',
    showAspButton && 'asp',
  ].filter(Boolean) as SectionKey[];

  // Nothing to configure means no reason to show the control.
  if (!sections.length) return null;

  const isExpanded = layout === 'expanded';

  // A popover wrapping a single control is pure indirection — it costs a click
  // to reach something the header row had room for anyway. Below two sections
  // the control goes straight in the row, the same rule the header applies to
  // its "..." overflow menu. Expanded mode is already flat, so it never inlines.
  const isInline = !isExpanded && sections.length === 1;

  const renderControl = (section: SectionKey) => {
    switch (section) {
      case 'mode':
        return (
          <AnalyticsModeToggle
            value={modeType}
            onChange={handleModeChange}
            isDisabled={isModeDisabled}
            disabledTooltip={REFERRING_DISABLED_FOR_SITES_TOOLTIP}
          />
        );
      case 'volume':
        return (
          <AnalyticsVolumeDropdown
            value={volumeTypes}
            onChange={handleVolumeTypesChange}
            isOptionDisabled={isVolumeTypeDisabled}
            disabledTooltip={SITES_DISABLED_FOR_REFERRING_TOOLTIP}
          />
        );
      case 'providerSites':
        return (
          <PulseToggle
            value={volumeTypes?.[0] || 'providers'}
            onChange={handleProviderSitesChange}
            options={PROVIDER_SITES_OPTIONS}
            alwaysShowLabels
            // Stretches to the panel width, but sizes to its content in the row.
            className={isInline ? undefined : 'w-full'}
            data-testid="analytics-provider-sites-toggle"
            aria-label="Toggle between Providers and Sites"
          />
        );
      case 'payerGroup':
        return (
          <PulseToggle
            value={payerGroupBy}
            onChange={handlePayerGroupByChange}
            options={PAYER_GROUP_BY_OPTIONS}
            alwaysShowLabels
            className={isInline ? undefined : 'w-full'}
            data-testid="analytics-payer-group-toggle"
            aria-label="Toggle between Payer Type and Payer Name"
          />
        );
      case 'asp':
        return <AnalyticsAspButton />;
    }
  };

  const labelledSections = sections.map((section) => (
    <div
      key={section}
      className="flex flex-col gap-1.5"
      data-testid={SECTION_TEST_IDS[section]}
    >
      <div className="text-secondary text-xs font-medium font-['Inter'] leading-4">
        {SECTION_LABELS[section]}
      </div>
      {renderControl(section)}
    </div>
  ));

  if (isExpanded) {
    return (
      <div
        className="flex flex-col justify-start items-stretch gap-4"
        data-testid="analytics-view-expanded"
      >
        {labelledSections}
      </div>
    );
  }

  if (isInline) {
    return (
      <div data-testid="analytics-view-inline">{renderControl(sections[0])}</div>
    );
  }

  return (
    <div ref={containerRef} className="relative" data-testid="analytics-view-button">
      <div
        ref={buttonRef}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label="Table options"
        className="px-4 bg-white rounded-full border border-neutral-200 flex justify-between
          items-center cursor-pointer hover:bg-neutral-100 h-9"
        onClick={handleToggleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleOpen();
          }
        }}
        data-testid="analytics-view-button-trigger"
      >
        <div className="flex justify-start items-center gap-1.5">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <Icon icon={faTableColumns} className="text-secondary w-4 h-4" />
          </div>
          <div className="text-primary text-sm font-medium font-['Inter'] leading-5 whitespace-nowrap">
            Options
          </div>
        </div>
        <Icon
          icon={faCaretDown}
          className={`text-neutral-500 w-4 h-4 ml-2 transition-all duration-300 ${
            isOpen ? '-rotate-90' : 'rotate-0'
          }`}
        />
      </div>

      {isOpen &&
        panelPosition &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Table options"
            className="p-4 fixed bg-white rounded-lg shadow-lg border border-neutral-200
              flex flex-col justify-start items-stretch gap-4 z-50 w-72"
            style={{
              top: `${panelPosition.top}px`,
              right: `${panelPosition.right}px`,
            }}
            data-testid="analytics-view-panel"
          >
            {labelledSections}
          </div>,
          document.body
        )}
    </div>
  );
};

export default AnalyticsViewButton;
